import type { GeoLocation } from '../data/geolocation'
import { LocationPicker } from './LocationPicker'
import styles from './SearchBar.module.css'

/** Location search ("Use my current location" + city/state/zip) depends on tracks having usable
 *  lat/lng on file, which isn't reliable sitewide yet - hidden until there's a geocoding backfill
 *  plan, rather than removed (location/radiusMi state, the API wiring, and LocationPicker itself
 *  all stay intact so this is a one-line flip back on). Toolbar.tsx's "Nearest" sort option is
 *  hidden alongside this for the same reason - it has nothing to sort by without a location set. */
const SHOW_LOCATION_SEARCH = false

export interface SearchBarProps {
  search: string
  onSearchChange: (value: string) => void
  location: GeoLocation | null
  radiusMi: number
  onLocationChange: (location: GeoLocation | null, radiusMi: number) => void
  when: 'any' | 'weekend' | '30days'
  onWhenChange: (when: 'any' | 'weekend' | '30days') => void
  totalCount: number
  isMobile: boolean
  onSubmit: () => void
}

/** The dark hero search section - "Find a race" + search box + location/radius + (desktop) a
 *  When select and Search button, per Main.dc.html (mobile) / Desktop.dc.html (desktop). */
export function SearchBar({ search, onSearchChange, location, radiusMi, onLocationChange, when, onWhenChange, totalCount, isMobile, onSubmit }: SearchBarProps) {
  return (
    <section className={styles.hero}>
      <div className={styles.heroTop}>
        <h1 className={styles.title}>Find a race</h1>
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <label className={styles.searchField}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            type="search"
            aria-label="Search events"
            placeholder="Event, track, city or series"
            className={styles.searchInput}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </label>

        {!isMobile ? (
          <>
            {SHOW_LOCATION_SEARCH ? <LocationPicker location={location} radiusMi={radiusMi} onChange={onLocationChange} /> : null}
            <label>
              <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>Dates</span>
              <select className={styles.whenSelect} value={when} onChange={(e) => onWhenChange(e.target.value as typeof when)}>
                <option value="any">Any time</option>
                <option value="weekend">This weekend</option>
                <option value="30days">Next 30 days</option>
              </select>
            </label>
            <button type="submit" className={styles.submit}>
              Search
            </button>
          </>
        ) : null}
      </form>

      {isMobile ? (
        <div className={styles.locationRow} style={SHOW_LOCATION_SEARCH ? undefined : { justifyContent: 'flex-end' }}>
          {SHOW_LOCATION_SEARCH ? <LocationPicker location={location} radiusMi={radiusMi} onChange={onLocationChange} /> : null}
          <span className={styles.eventCount}>{totalCount} events</span>
        </div>
      ) : null}
    </section>
  )
}
